import React from 'react';
import Icon from './InsightsIcon';
import { sectionData, insightLabels, reportDate } from './insightsModel';
import './Insights.css';
import SubscriberDirectory from './SubscriberDirectory';

const number = (value) => Number(value || 0).toLocaleString();
const objectUrl = (id) => `/objects?preview=${encodeURIComponent(id)}`;
const batchColor = (name) =>
  name === 'accepted'
    ? 'accepted'
    : name === 'partial'
    ? 'partial'
    : ['failed', 'not_sent', 'delivery_uncertain'].includes(name)
    ? 'failed'
    : 'archived';

function Heading({ kicker, title, action }) {
  return (
    <div className="panel-head">
      <div>
        <span className="section-kicker">{kicker}</span>
        <h2>{title}</h2>
      </div>
      {action}
    </div>
  );
}

function Unavailable({ name, onRetry }) {
  return (
    <div className="section-error" role="status">
      <span className="state-icon small">
        <Icon name="warning" size={22} />
      </span>
      <h3>{name} is temporarily unavailable.</h3>
      <p>Other available reports are still shown. Please try again.</p>
      <button className="button secondary" onClick={onRetry}>
        <Icon name="refresh" size={17} />
        Retry report
      </button>
    </div>
  );
}

function Panel({ report, section, className = '', title, onRetry, children }) {
  return (
    <article className={`panel ${className}`}>
      {report?.sections?.[section]?.status === 'ready' ? (
        children
      ) : (
        <Unavailable name={title} onRetry={onRetry} />
      )}
    </article>
  );
}

function Loading() {
  return (
    <div
      className="loading-state"
      aria-label="Loading insights"
      aria-busy="true"
    >
      <div className="skeleton sk-wide" />
      <div className="skeleton sk-side" />
      <div className="skeleton sk-full" />
      <div className="skeleton sk-wide low" />
      <div className="skeleton sk-side low" />
      <span className="sr-only">Loading your insights</span>
    </div>
  );
}

function EmptyCollection() {
  return (
    <div className="empty-layout">
      <div className="empty-hero">
        <div className="empty-art" aria-hidden="true">
          <span />
          <span />
          <span />
          <i />
        </div>
        <span className="section-kicker">Your story starts here</span>
        <h2>Insights grow with your collection.</h2>
        <p>
          Publish your first LifeObject, then share it through the discovery
          requirements that make sense for your audience.
        </p>
        <a className="button primary" href="/objects?create=1">
          <Icon name="plus" size={18} />
          Create your first LifeObject
        </a>
      </div>
      <div className="empty-steps">
        {[
          [
            'Make something meaningful',
            'Add authored text, media, or a LifeBot.',
          ],
          [
            'Choose how it’s discovered',
            'Choose the audience, place, code, device, media, or time requirements you need.',
          ],
          [
            'Learn from intentional signals',
            'See comments, likes, saves, shares, and notification acceptance—not private recipient tracking.',
          ],
        ].map(([title, copy], i) => (
          <div key={title}>
            <span>0{i + 1}</span>
            <strong>{title}</strong>
            <p>{copy}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function InsightsView({
  report,
  days,
  loading,
  error,
  onRetry,
  onPeriod,
  onExport,
}) {
  const [audienceOpen, setAudienceOpen] = React.useState(false);
  const collection = sectionData(report, 'collection');
  const audience = sectionData(report, 'subscribers');
  const notifications = sectionData(report, 'notifications');
  const engagement = sectionData(report, 'engagement');
  const resources = sectionData(report, 'requirements');
  const period = report?.days || days;
  const states = Object.entries(collection?.states || {});
  const dates = (collection?.created || []).slice(-3);
  const maxCreated = Math.max(1, ...dates.map((row) => row.count));
  const batches = Object.entries(notifications?.states || {});
  const upcoming = notifications?.upcoming || [];
  const mix = collection?.content || [];
  const maxMix = Math.max(1, ...mix.map((row) => row.count));
  const conversations = engagement?.conversations || [];
  const topConversation = conversations[0];
  return (
    <div className="lt-insights">
      <a className="skip-link" href="#insights-report">
        Skip to insights
      </a>
      <div className="topline">
        <div>
          <div className="eyebrow">Studio / Insights</div>
          <h1>Understand what resonates.</h1>
          <p className="lede">
            A clear view of your collection, your audience, and the intentional
            ways people engage.
          </p>
        </div>
        <div className="top-actions">
          <a
            className="button secondary"
            href="/objects?create=1&delivery=push"
          >
            <Icon name="bell" size={18} />
            Create notification
          </a>
          <a className="button primary" href="/objects?create=1">
            <Icon name="plus" size={18} />
            Create LifeObject
          </a>
        </div>
      </div>
      <div className="control-bar">
        <div
          className="period-control"
          role="group"
          aria-label="Activity period"
        >
          <span>Activity period</span>
          <div className="segmented">
            {[7, 30, 90].map((value) => (
              <button
                key={value}
                className={days === value ? 'selected' : ''}
                aria-pressed={days === value}
                onClick={() => onPeriod(value)}
              >
                {value} days
              </button>
            ))}
          </div>
          <span className="scope-note">
            Collection and audience totals are current
          </span>
        </div>
        <div className="data-actions">
          <div className="updated">
            <span className={`status-dot ${error ? 'cached' : ''}`} />
            <span>
              {report
                ? `Updated ${reportDate(report.generatedAt)}`
                : 'Waiting for your report'}
            </span>
          </div>
          <button
            className="icon-button labeled"
            onClick={onRetry}
            aria-label="Refresh insights"
            disabled={loading}
          >
            <Icon name="refresh" size={18} />
            <span>Refresh</span>
          </button>
          <button
            className="icon-button labeled"
            onClick={onExport}
            aria-label="Export aggregate CSV"
            disabled={!report || loading}
          >
            <Icon name="download" size={18} />
            <span>Export</span>
          </button>
        </div>
      </div>
      {error && report && (
        <div className="truth-note" role="alert">
          <Icon name="warning" size={18} />
          <span>
            {error} Showing the last available {report.days}-day report.
          </span>
          <button className="text-button" onClick={onRetry}>
            Retry
          </button>
        </div>
      )}
      <div
        id="insights-report"
        className={`dashboard ${loading && report ? 'is-refreshing' : ''}`}
        aria-busy={loading}
      >
        {loading && report && (
          <div className="refresh-banner" role="status">
            <span className="spinner" />
            Refreshing while you keep reading this snapshot…
          </div>
        )}
        {!report ? (
          loading ? (
            <Loading />
          ) : (
            <div className="full-state error-state" role="alert">
              <span className="state-icon">
                <Icon name="warning" size={28} />
              </span>
              <span className="section-kicker">Insights unavailable</span>
              <h2>We couldn’t gather your view.</h2>
              <p>
                Your content and settings are safe. Try again to reconnect to
                your latest report.
              </p>
              <button className="button primary" onClick={onRetry}>
                <Icon name="refresh" size={17} />
                Try again
              </button>
            </div>
          )
        ) : (
          <>
            {collection?.total === 0 && <EmptyCollection />}
            <section
              className="overview-grid"
              aria-label="Collection and audience"
            >
              <Panel
                {...{ report, onRetry }}
                section="collection"
                title="Collection"
                className="collection-panel"
              >
                <Heading
                  kicker="Your collection"
                  title="What have I published?"
                  action={
                    <a className="text-button" href="/objects">
                      View collection <Icon name="arrow" size={16} />
                    </a>
                  }
                />
                <div className="collection-body">
                  <div className="big-number">
                    <strong>{number(collection?.total)}</strong>
                    <span>LifeObjects in your collection</span>
                    <small>
                      Includes {number(collection?.states?.archived)} archived
                    </small>
                  </div>
                  <div className="status-visual">
                    <div
                      className="status-bar"
                      role="img"
                      aria-label={`Collection status: ${
                        states
                          .map(
                            ([name, count]) =>
                              `${number(count)} ${insightLabels[name] || name}`
                          )
                          .join(', ') || 'No LifeObjects yet'
                      }`}
                    >
                      {states.map(([name, count]) => (
                        <span
                          key={name}
                          className={
                            name === 'active'
                              ? 'published'
                              : name === 'draft'
                              ? 'draft'
                              : 'archived'
                          }
                          style={{ flex: count }}
                        />
                      ))}
                    </div>
                    <div className="legend roomy">
                      {states.map(([name, count]) => (
                        <div key={name}>
                          <i
                            className={`dot ${
                              name === 'active'
                                ? 'published'
                                : name === 'draft'
                                ? 'draft'
                                : 'archived'
                            }`}
                          />
                          <span>{insightLabels[name] || name}</span>
                          <strong>{number(count)}</strong>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
                <div className="collection-footer">
                  <span>Created</span>
                  <div className="creation-bars">
                    {dates.length ? (
                      dates.map((row) => (
                        <div key={row._id}>
                          <i
                            style={{
                              width: `${(row.count / maxCreated) * 100}%`,
                            }}
                          />
                          <span>{row._id}</span>
                          <strong>{number(row.count)}</strong>
                        </div>
                      ))
                    ) : (
                      <p className="report-empty">
                        No LifeObjects created in this period.
                      </p>
                    )}
                  </div>
                </div>
                <p className="report-note">
                  Recent creation dates in the selected {period}-day period.
                </p>
              </Panel>
              <Panel
                {...{ report, onRetry }}
                section="subscribers"
                title="Audience"
                className="audience-panel"
              >
                <Heading kicker="Your audience" title="Who can I reach?" />
                <button
                  className="button small secondary"
                  onClick={() => setAudienceOpen(true)}
                >
                  View followers & subscribers
                </button>
                <div className="audience-total">
                  <strong>{number(audience?.total)}</strong>
                  <span>unique followers &amp; subscribers</span>
                </div>
                <div className="audience-channels">
                  <div>
                    <span className="channel-icon app">LT</span>
                    <p>
                      <strong>{number(audience?.appFollows)}</strong>
                      <span>App-account follows</span>
                    </p>
                  </div>
                  <div>
                    <span className="channel-icon browser">◎</span>
                    <p>
                      <strong>{number(audience?.browserSubscriptions)}</strong>
                      <span>Browser subscriptions</span>
                    </p>
                  </div>
                </div>
                <p className="footnote">
                  The combined total counts known accounts once. Some older
                  follows have no recorded channel, while a person may follow in
                  both places. Channel counts aren’t a breakdown of the total.
                  Conversation follows are separate.
                </p>
              </Panel>
            </section>
            <SubscriberDirectory
              open={audienceOpen}
              onClose={() => setAudienceOpen(false)}
            />
            <Panel
              {...{ report, onRetry }}
              section="notifications"
              title="Notifications"
              className="notification-panel"
            >
              <Heading
                kicker={`Notifications · last ${period} days`}
                title="Are notifications being accepted?"
                action={
                  <a
                    className="button small secondary"
                    href="/objects?create=1&delivery=push"
                  >
                    <Icon name="plus" size={16} />
                    New notification
                  </a>
                }
              />
              <div className="notification-grid">
                <div className="acceptance">
                  <div className="acceptance-summary">
                    <div>
                      <strong>{number(notifications?.accepted)}</strong>
                      <span>Provider accepted</span>
                    </div>
                    <div>
                      <strong>{number(notifications?.failed)}</strong>
                      <span>Provider failed</span>
                    </div>
                  </div>
                  <p className="report-note">Notification batch status</p>
                  <div
                    className="acceptance-bar"
                    role="img"
                    aria-label={`Notification batch status: ${
                      batches
                        .map(
                          ([name, count]) =>
                            `${number(count)} ${insightLabels[name] || name}`
                        )
                        .join(', ') || 'No notification batches in this period'
                    }`}
                  >
                    {batches.map(([name, count]) => (
                      <span
                        key={name}
                        className={batchColor(name)}
                        style={{ flex: count }}
                      />
                    ))}
                  </div>
                  <div className="legend inline">
                    {batches.map(([name, count]) => (
                      <span key={name}>
                        <i className={`dot ${batchColor(name)}`} />
                        {insightLabels[name] || name} · {number(count)}
                      </span>
                    ))}
                  </div>
                  {!batches.length && (
                    <p className="report-empty">
                      No notifications sent in this period.
                    </p>
                  )}
                  <div className="truth-note">
                    <Icon name="help" size={17} />
                    <span>
                      Provider acceptance means a notification service accepted
                      the request. It does not confirm delivery, an open, or a
                      read.
                    </span>
                  </div>
                </div>
                <div className="scheduled">
                  <div className="subhead">
                    <h3>Coming up</h3>
                    <span>{number(upcoming.length)} scheduled</span>
                  </div>
                  {upcoming.length ? (
                    upcoming.map((item) => {
                      const date = new Date(item.scheduledAt);
                      const id =
                        typeof item.lifeObjectId === 'string'
                          ? item.lifeObjectId
                          : item.lifeObjectId?.objectId;
                      const Tag = id ? 'a' : 'div';
                      return (
                        <Tag
                          className="schedule-row"
                          key={item._id}
                          {...(id ? { href: objectUrl(id) } : {})}
                        >
                          <span className="date-tile">
                            <strong>
                              {Number.isNaN(date.getTime())
                                ? '—'
                                : date.getDate()}
                            </strong>
                            <small>
                              {Number.isNaN(date.getTime())
                                ? ''
                                : date.toLocaleDateString(undefined, {
                                    month: 'short',
                                  })}
                            </small>
                          </span>
                          <span>
                            <strong>
                              {item.title || 'Scheduled notification'}
                            </strong>
                            <small>{reportDate(item.scheduledAt)}</small>
                          </span>
                          <span className="status scheduled-status">
                            Scheduled
                          </span>
                          {id && <Icon name="chevron" size={16} />}
                        </Tag>
                      );
                    })
                  ) : (
                    <p className="report-empty">No upcoming notifications.</p>
                  )}
                </div>
              </div>
            </Panel>
            <section className="lower-grid" aria-label="Engagement">
              <Panel
                {...{ report, onRetry }}
                section="engagement"
                title="Engagement"
                className="engagement-panel"
              >
                <Heading
                  kicker={`Intentional engagement · last ${period} days`}
                  title="What are people choosing to engage with?"
                />
                <div className="engagement-metrics">
                  {[
                    ['comments', 'Comments', engagement?.comments],
                    ['heart', 'Likes', engagement?.reactions?.like],
                    [
                      'heart',
                      'Comment likes',
                      engagement?.reactions?.['comment-like'],
                    ],
                    ['bookmark', 'Saves', engagement?.interactions?.saved],
                    ['share', 'Shares', engagement?.interactions?.shared],
                  ].map(([icon, label, count]) => (
                    <div key={label}>
                      <Icon name={icon} size={18} />
                      <strong>{number(count)}</strong>
                      <span>{label}</span>
                    </div>
                  ))}
                </div>
                <div className="discussed-head">
                  <h3>Most discussed</h3>
                  <span>Comments—not read receipts</span>
                </div>
                <div className="discussed-list">
                  {conversations.length ? (
                    conversations.map((item, index) => (
                      <a key={item.id} href={objectUrl(item.id)}>
                        <span
                          className={`object-thumb ${
                            ['sage', 'blue', 'amber'][index % 3]
                          }`}
                        >
                          {String(index + 1).padStart(2, '0')}
                        </span>
                        <span>
                          <strong>{item.name || 'LifeObject'}</strong>
                          <small>Open LifeObject</small>
                        </span>
                        <span className="discussion-count">
                          <strong>{number(item.comments)}</strong>
                          <small>comments</small>
                        </span>
                        <Icon name="arrow" size={17} />
                      </a>
                    ))
                  ) : (
                    <p className="report-empty">
                      No comments recorded for this period.
                    </p>
                  )}
                </div>
              </Panel>
            </section>
            <article className="panel inventory-panel">
              <Heading
                kicker="Collection anatomy"
                title="What makes discovery possible?"
                action={
                  <span className="configuration-label">
                    Configured inventory, not visitors
                  </span>
                }
              />
              <div className="inventory-body">
                <div className="content-mix">
                  <h3>Content item mix</h3>
                  {collection ? (
                    <>
                      <div className="mix-list">
                        {mix.map((row) => (
                          <div key={row._id}>
                            <span>
                              {{
                                html: 'HTML',
                                text: 'Text',
                                image: 'Images',
                                video: 'Video',
                                audio: 'Audio',
                                pdf: 'PDF',
                              }[row._id] || row._id}
                            </span>
                            <i>
                              <b
                                style={{
                                  width: `${(row.count / maxMix) * 100}%`,
                                }}
                              />
                            </i>
                            <strong>{number(row.count)}</strong>
                          </div>
                        ))}
                      </div>
                      {!mix.length && (
                        <p className="report-empty">No content items yet.</p>
                      )}
                      <p>
                        Counts are media items. A LifeObject can contain more
                        than one. {number(collection.lifeBots)} configured
                        LifeBots.
                      </p>
                    </>
                  ) : (
                    <Unavailable name="Content mix" onRetry={onRetry} />
                  )}
                </div>
                <div className="resource-inventory">
                  <h3>Registered discovery resources</h3>
                  {resources ? (
                    resources.length ? (
                      <div className="resource-grid">
                        {resources.map((row) => (
                          <div key={row._id}>
                            <span className="resource-symbol">
                              {{
                                qr: '⌗',
                                ble: '⌁',
                                audio: '∿',
                                'geo-point': '◎',
                                'geo-fence': '◎',
                              }[row._id] || '◇'}
                            </span>
                            <strong>{number(row.count)}</strong>
                            <span>{insightLabels[row._id] || row._id}</span>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="report-empty">
                        No discovery resources registered yet.
                      </p>
                    )
                  ) : (
                    <Unavailable name="Discovery resources" onRetry={onRetry} />
                  )}
                </div>
              </div>
            </article>
            <section className="next-step">
              <div className="next-ornament" aria-hidden="true">
                <span />
                <span />
                <span />
              </div>
              <div>
                <span className="section-kicker light">
                  A thoughtful next step
                </span>
                <h2>
                  {topConversation
                    ? 'Turn your most-discussed story into a series.'
                    : 'Leave something worth discovering.'}
                </h2>
                <p>
                  {topConversation
                    ? `“${
                        topConversation.name || 'Your LifeObject'
                      }” is prompting conversation. Build on that intentional interest with a related LifeObject.`
                    : 'Create a LifeObject for your audience, then choose where and when it can be discovered.'}
                </p>
              </div>
              <a className="button light-button" href="/objects?create=1">
                Create a LifeObject <Icon name="arrow" size={17} />
              </a>
            </section>
          </>
        )}
      </div>
      <p className="report-note">
        Activity covers {period} days. Collection and audience totals are
        current. Reports may be cached for 30 seconds.
      </p>
    </div>
  );
}
